import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE } from '../config';
import { CreditCard, Save, RefreshCcw, Info, Eye, EyeOff } from 'lucide-react';
import { Alert, AlertColor, Snackbar, CircularProgress, TextField, Button } from '@mui/material';

/**
 * PLAN CARDS — edits the text printed on the plan cards in the app and webapp.
 *
 * This screen is display-only by design: nothing here feeds the payout engine.
 * Interest actually paid keeps coming from Business Rules / the cron. Changing a
 * card changes what users read on the Plans screen, nothing else.
 */

interface PlanCard {
	_id?: string;
	key: string;
	name: string;
	startingAmount: string;
	currency: string;
	monthlyReturn: string;
	investmentRange: string;
	capitalBack: string;
	biWeeklyReturn: string;
	maxReturn: string;
	comparisonRange: string;
	comparisonMaxReturn: string;
	landingTitle: string;
	landingRange: string;
	badge: string;
	features: string[];
	isPopular: boolean;
	order: number;
	visible: boolean;
}

const FIELDS: { name: keyof PlanCard; label: string; hint?: string }[] = [
	{ name: 'name', label: 'Plan Name', hint: 'Card heading, e.g. "Basic Plan"' },
	{ name: 'startingAmount', label: 'Starting Amount', hint: 'Shown as the big number, e.g. "500"' },
	{ name: 'currency', label: 'Currency', hint: 'e.g. "USDT"' },
	{ name: 'monthlyReturn', label: 'Monthly Return', hint: 'e.g. "+5.5%"' },
	{ name: 'investmentRange', label: 'Investment Range', hint: 'e.g. "500-2,000 USDT"' },
	{ name: 'capitalBack', label: 'Capital Back', hint: '"Yes" or "No"' },
	{ name: 'biWeeklyReturn', label: 'Bi-Weekly Return', hint: 'e.g. "2.75%"' },
	{ name: 'maxReturn', label: 'Yearly / Max Return', hint: 'e.g. "66%"' },
	{ name: 'comparisonRange', label: 'Range (comparison table)', hint: 'Blank = use Investment Range' },
	{ name: 'comparisonMaxReturn', label: 'Max Return (comparison table)', hint: 'Blank = use Yearly / Max Return' },
	{ name: 'landingTitle', label: 'Title (landing page)', hint: 'Short name, e.g. "Basic". Blank = use Plan Name' },
	{ name: 'landingRange', label: 'Investment (landing page)', hint: 'e.g. "$500-$2,000". Blank = use Investment Range' },
	{ name: 'badge', label: 'Badge', hint: 'e.g. "Most Popular" (web only)' },
];

const PlanCards: React.FC = () => {
	const [cards, setCards] = useState<PlanCard[]>([]);
	const [loading, setLoading] = useState(true);
	const [savingKey, setSavingKey] = useState<string | null>(null);
	const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: AlertColor }>({
		open: false,
		message: '',
		severity: 'success',
	});

	const showSnackbar = (message: string, severity: AlertColor = 'success') =>
		setSnackbar({ open: true, message, severity });

	useEffect(() => {
		fetchCards();
	}, []);

	const fetchCards = async () => {
		setLoading(true);
		try {
			const token = localStorage.getItem('token');
			const res = await axios.get(`${API_BASE}/api/plan-cards`, {
				headers: { Authorization: `Bearer ${token}` },
			});
			if (res.data.status === 'success' && Array.isArray(res.data.data)) {
				setCards(res.data.data);
			}
		} catch (error) {
			showSnackbar('Could not load plan cards', 'error');
		} finally {
			setLoading(false);
		}
	};

	const updateField = (key: string, field: keyof PlanCard, value: any) =>
		setCards((prev) => prev.map((c) => (c.key === key ? { ...c, [field]: value } : c)));

	const saveCard = async (card: PlanCard) => {
		setSavingKey(card.key);
		try {
			const token = localStorage.getItem('token');
			const { _id, key, ...payload } = card;
			const res = await axios.put(`${API_BASE}/api/plan-cards/${key}`, payload, {
				headers: { Authorization: `Bearer ${token}` },
			});
			if (res.data.status === 'success') {
				showSnackbar(`${card.name || key} saved — live on app & webapp`, 'success');
			} else {
				showSnackbar('Save failed', 'error');
			}
		} catch (error) {
			showSnackbar('Save failed', 'error');
		} finally {
			setSavingKey(null);
		}
	};

	return (
		<div className="p-6 max-w-7xl mx-auto">
			<div className="flex items-center justify-between mb-4">
				<div className="flex items-center gap-3">
					<CreditCard className="w-7 h-7 text-purple-700" />
					<div>
						<h1 className="text-2xl font-bold text-gray-800">Plan Cards</h1>
						<p className="text-sm text-gray-500">Text shown on the Plans screen in the app and webapp</p>
					</div>
				</div>
				<Button variant="outlined" startIcon={<RefreshCcw className="w-4 h-4" />} onClick={fetchCards}>
					Reload
				</Button>
			</div>

			<div className="flex gap-2 items-start bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6">
				<Info className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
				<p className="text-sm text-amber-800">
					These are <b>display values only</b>. They do not change any interest that is actually paid — payouts
					are driven by Business Rules and the cron, completely separately. Whatever you type here is exactly
					what users will read on the plan cards, so keep it consistent with the real rates.
				</p>
			</div>

			{loading ? (
				<div className="flex justify-center py-16">
					<CircularProgress />
				</div>
			) : cards.length === 0 ? (
				<p className="text-gray-500">No plan cards found.</p>
			) : (
				<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
					{cards.map((card) => (
						<div key={card.key} className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
							<div className="flex items-center justify-between mb-4">
								<span className="text-xs font-semibold uppercase tracking-wide text-purple-700 bg-purple-50 px-2 py-1 rounded">
									{card.key}
								</span>
								<button
									type="button"
									onClick={() => updateField(card.key, 'visible', !card.visible)}
									className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
									title={card.visible ? 'Visible to users' : 'Hidden from users'}
								>
									{card.visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
									{card.visible ? 'Visible' : 'Hidden'}
								</button>
							</div>

							<div className="space-y-3">
								{FIELDS.map((f) => (
									<TextField
										key={String(f.name)}
										label={f.label}
										helperText={f.hint}
										value={(card[f.name] as string) ?? ''}
										onChange={(e) => updateField(card.key, f.name, e.target.value)}
										size="small"
										fullWidth
									/>
								))}

								<TextField
									label="Features (one per line)"
									value={(card.features || []).join('\n')}
									onChange={(e) =>
										updateField(
											card.key,
											'features',
											e.target.value.split('\n').map((s) => s.trim()).filter(Boolean)
										)
									}
									size="small"
									fullWidth
									multiline
									minRows={3}
									helperText="Bullet points on the web card"
								/>

								<div className="flex gap-3">
									<TextField
										label="Order"
										type="number"
										value={card.order ?? 0}
										onChange={(e) => updateField(card.key, 'order', Number(e.target.value))}
										size="small"
										fullWidth
									/>
									<label className="flex items-center gap-2 text-sm text-gray-700 whitespace-nowrap">
										<input
											type="checkbox"
											checked={!!card.isPopular}
											onChange={(e) => updateField(card.key, 'isPopular', e.target.checked)}
										/>
										Popular
									</label>
								</div>
							</div>

							<Button
								variant="contained"
								fullWidth
								className="!mt-5"
								disabled={savingKey === card.key}
								startIcon={
									savingKey === card.key ? (
										<CircularProgress size={16} color="inherit" />
									) : (
										<Save className="w-4 h-4" />
									)
								}
								onClick={() => saveCard(card)}
							>
								{savingKey === card.key ? 'Saving…' : 'Save'}
							</Button>
						</div>
					))}
				</div>
			)}

			<Snackbar
				open={snackbar.open}
				autoHideDuration={4000}
				onClose={() => setSnackbar({ ...snackbar, open: false })}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
			>
				<Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</div>
	);
};

export default PlanCards;
